import { apiClient } from './client';

/* analytics.sdics.tech — every figure comes from /api/analytics/* (see
   backend-go/internal/analytics). Nothing here is computed client-side
   beyond formatting. */

/** Area name the API uses for registered voters NRB has no record of. */
export const NOT_IN_NRB = '(not in NRB)';

export type AnalyticsLevel = 'county' | 'subcounty' | 'division' | 'location';
export const ANALYTICS_LEVELS: AnalyticsLevel[] = ['county', 'subcounty', 'division', 'location'];

export interface AnalyticsFilters {
  county: string;
  subcounty: string;
  division: string;
  location: string;
  date_from: string; // YYYY-MM-DD or ''
  date_to: string;
}

export const EMPTY_ANALYTICS_FILTERS: AnalyticsFilters = {
  county: '',
  subcounty: '',
  division: '',
  location: '',
  date_from: '',
  date_to: '',
};

export interface AnalyticsFigures {
  name?: string;
  registered: number;
  unregistered: number;
  total: number;
  registered_pct: number;
  registered_not_in_nrb: number;
  registered_via_app: number;
}

export interface AnalyticsOptions {
  level: AnalyticsLevel | '';
  values: string[];
  date_from: string | null;
  date_to: string | null;
}

export interface AnalyticsTrendPoint {
  period: string;
  registered: number;
}

export type TrendInterval = 'day' | 'month' | 'year';

export interface AnalyticsPerson {
  national_id: string;
  found: boolean;
  status?: 'REGISTERED' | 'UNREGISTERED';
  full_name?: string;
  county?: string;
  subcounty?: string;
  division?: string;
  location?: string;
  registration_date: string | null;
  in_nrb: boolean;
  registered_via_app: boolean;
  tribe?: string;
  constituency?: string;
  ward?: string;
  polling_centre?: string;
}

export interface AnalyticsLookupResult {
  counts: { registered: number; unregistered: number; not_found: number };
  results: AnalyticsPerson[];
}

/** The filters as query parameters; area levels only down to the first unset one. */
function toQs(f: Partial<AnalyticsFilters>, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams();
  for (const l of ANALYTICS_LEVELS) {
    const v = f[l];
    if (!v) break;
    p.set(l, v);
  }
  if (f.date_from) p.set('date_from', f.date_from);
  if (f.date_to) p.set('date_to', f.date_to);
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
}

const BASE = 'analytics';

/* ---- Registered voters: the roll's IEBC tree ---- */

export type RegLevel = 'county' | 'constituency' | 'ward' | 'polling_centre';
export const REG_LEVELS: RegLevel[] = ['county', 'constituency', 'ward', 'polling_centre'];

export interface RegFilters {
  county: string;
  constituency: string;
  ward: string;
  polling_centre: string;
  date_from: string;
  date_to: string;
}

export interface RegRowApi {
  name: string;
  registered: number;
  centres: number;
  male: number;
  female: number;
  sex_not_recorded: number;
  youth: number;
  age_recorded: number;
}

export type RegPivotBy = 'day' | 'week' | 'month' | 'year' | 'sex' | 'age' | 'tribe';

export interface RegPivotApi {
  columns: string[];
  rows: { key: string; values: number[] }[];
  monthly: boolean;
}

function regQs(f: Partial<RegFilters>, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams();
  for (const l of REG_LEVELS) {
    const v = f[l];
    if (!v) break;
    p.set(l, v);
  }
  if (f.date_from) p.set('date_from', f.date_from);
  if (f.date_to) p.set('date_to', f.date_to);
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const registeredApi = {
  options(level: RegLevel, f: Partial<RegFilters>): Promise<AnalyticsOptions> {
    return apiClient.get(`${BASE}/registered/options/${regQs({ ...f, date_from: '', date_to: '' }, { level })}`);
  },
  rows(f: RegFilters, group?: RegLevel): Promise<{ level: RegLevel; rows: RegRowApi[] }> {
    return apiClient.get(`${BASE}/registered/rows/${regQs(f, group ? { group } : {})}`);
  },
  pivot(f: RegFilters, rows: RegLevel, by: RegPivotBy): Promise<{ pivot: RegPivotApi }> {
    return apiClient.get(`${BASE}/registered/pivot/${regQs(f, { rows, by })}`);
  },
};

/* ---- ID holders by sex / age group / tribe (NRB records, as loaded) ---- */

export type IdDemoBy = 'sex' | 'age' | 'tribe';

export interface IdPivotApi {
  columns: string[];
  rows: { key: string; registered: number[]; unregistered: number[] }[];
}

export const idsApi = {
  /** grouped=false gives one "Total" row. Dates don't apply. */
  pivot(f: Partial<AnalyticsFilters>, by: IdDemoBy, grouped = true): Promise<{ pivot: IdPivotApi }> {
    return apiClient.get(`${BASE}/ids/pivot/${toQs({ ...f, date_from: '', date_to: '' }, grouped ? { by } : { by, rows: 'none' })}`);
  },
};

export const analyticsApi = {
  summary(f: AnalyticsFilters): Promise<{ summary: AnalyticsFigures }> {
    return apiClient.get(`${BASE}/summary/${toQs(f)}`);
  },
  breakdown(f: AnalyticsFilters): Promise<{ level: AnalyticsLevel | ''; rows: AnalyticsFigures[] }> {
    return apiClient.get(`${BASE}/breakdown/${toQs(f)}`);
  },
  trend(f: AnalyticsFilters, interval: TrendInterval): Promise<{ points: AnalyticsTrendPoint[] }> {
    return apiClient.get(`${BASE}/trend/${toQs(f, { interval })}`);
  },
  /** Choices for the level below the deepest one set in f (dates ignored). */
  options(f: Partial<AnalyticsFilters>): Promise<AnalyticsOptions> {
    return apiClient.get(`${BASE}/options/${toQs({ ...f, date_from: '', date_to: '' })}`);
  },
  lookup(ids: string): Promise<AnalyticsLookupResult> {
    const body = new URLSearchParams({ ids });
    return apiClient.post(`${BASE}/lookup/`, body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },
};

/* ---- Reports: person-level downloads (at most EXPORT_LIMIT records each) ---- */

export type ExportStatus = 'unregistered' | 'registered' | 'all';

export interface ExportFilters {
  status: ExportStatus;
  county: string;
  subcounty: string;
  division: string;
  location: string;
  sub_location: string;
  village: string;
  date_from: string; // registered only
  date_to: string;
  with_phone: boolean;
  id_number: string; // exact
  name: string; // part of the name
  phone: string; // part of the phone number
  clan: string; // part of the clan
  family: string; // part of the family name
}

export const EXPORT_LIMIT = 1_000_000;

export interface ExportCount { count: number; over_limit: boolean; limit: number }

function exportQs(f: ExportFilters, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams({ status: f.status });
  for (const l of ['county', 'subcounty', 'division', 'location', 'sub_location', 'village'] as const) {
    if (!f[l]) break;
    p.set(l, f[l]);
  }
  if (f.status !== 'unregistered') {
    if (f.date_from) p.set('date_from', f.date_from);
    if (f.date_to) p.set('date_to', f.date_to);
  }
  if (f.with_phone) p.set('with_phone', '1');
  if (f.id_number) p.set('id_number', f.id_number);
  if (f.name) p.set('name', f.name);
  if (f.phone) p.set('phone', f.phone);
  if (f.clan) p.set('clan', f.clan);
  if (f.family) p.set('family', f.family);
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  return `?${p}`;
}

export const exportApi = {
  count(f: ExportFilters): Promise<ExportCount> {
    return apiClient.get(`${BASE}/export/count/${exportQs(f)}`);
  },
  options(level: 'sub_location' | 'village', f: ExportFilters): Promise<{ values: string[] }> {
    return apiClient.get(`${BASE}/export/options/${exportQs({ ...f, status: 'all', date_from: '', date_to: '', with_phone: false, id_number: '', name: '', phone: '', clan: '', family: '' }, { level })}`);
  },
  preview(f: ExportFilters): Promise<{ columns: string[]; rows: string[][] }> {
    return apiClient.get(`${BASE}/export/preview/${exportQs(f)}`);
  },
  /** Downloads the PDF and saves it; onProgress gets the bytes received so far. */
  async download(f: ExportFilters, onProgress: (bytes: number) => void): Promise<{ fileName: string; records: number }> {
    try {
      const res = await apiClient.client.get<Blob>(`${BASE}/export/download/${exportQs(f)}`, {
        responseType: 'blob',
        timeout: 0, // a million records takes minutes
        onDownloadProgress: (e) => onProgress(e.loaded),
      });
      const fileName = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '')?.[1] || 'report.pdf';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
      return { fileName, records: Number(res.headers['x-record-count'] || 0) };
    } catch (e: any) {
      // errors come back as a Blob too: read the server's message out of it
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try {
          const body = JSON.parse(await blob.text());
          throw new Error(body?.error?.message || 'Download failed');
        } catch (inner: any) {
          throw inner instanceof Error ? inner : new Error('Download failed');
        }
      }
      throw new Error(e?.message || 'Download failed');
    }
  },
};
